import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMain } from '../../hooks/useMain';

const norm = (s) => (s || '').toString().trim().toLowerCase();
const normNumber = (s) => (s || '').toString().replace(/\D/g, '');

const TEMPLATE_HEADERS = [
  'clientName',
  'clientNumber',
  'clientEmail',
  'address',
  'gender',
  'birthday',
  'anniversary',
];

const getField = (row, key) => {
  if (row[key] !== undefined) return row[key];
  const rowKeys = Object.keys(row);
  const match = rowKeys.find((k) => norm(k) === norm(key));
  return match !== undefined ? row[match] : undefined;
};

// duplicate WITHIN the file = same clientNumber appearing more than once
const duplicateKey = (r) => normNumber(r.clientNumber);

/**
 * existingClients: the clients already saved in the branch (pass in from
 * AllClients.jsx, e.g. `clients` from redux/fetchAllClients).
 * We flag any uploaded row whose clientNumber already exists there.
 */
const BulkUploadClientDialog = ({ onUploaded, existingClients = [], branchData }) => {
  const { activeBranchId, createManyClient, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const fileInputRef = useRef(null);

  const existingNumberSet = useMemo(
    () => new Set(existingClients.map((c) => normNumber(c.clientNumber))),
    [existingClients]
  );

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const downloadExcelTemplate = () => {
    const headers = [...TEMPLATE_HEADERS];
    if (branchData?.gstNumber) headers.push('clientGstNumber', 'clientPos');

    const sampleData = [
      {
        clientName: 'test 4 branch',
        clientNumber: '1234567890',
        clientEmail: 'client1@gmail.com',
        address: 'xyz',
        gender: 'male',
        birthday: '18/11/2022',
        anniversary: '10/11/2022',
      },
      {
        clientName: 'test 5 branch',
        clientNumber: '1234567891',
        clientEmail: 'client2@gmail.com',
        address: 'xyz',
        gender: 'female',
        birthday: '19/11/2022',
        anniversary: '11/11/2022',
      },
    ];
    if (branchData?.gstNumber) {
      sampleData.forEach((row) => {
        row.clientGstNumber = '07AAAAA0000A1Z5';
        row.clientPos = 'Delhi (07)';
      });
    }

    const ws = XLSX.utils.json_to_sheet(sampleData, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, 'client_template.xlsx');
  };

  const buildFlags = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.invalid) return;
      const key = duplicateKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.map((r) => ({
      ...r,
      isDuplicateInFile: !r.invalid && counts.get(duplicateKey(r)) > 1,
      alreadyExists: !r.invalid && existingNumberSet.has(normNumber(r.clientNumber)),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!jsonData.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        let mapped = jsonData.map((item, idx) => {
          const clientName = (getField(item, 'clientName') || '').toString().trim();
          const clientNumber = (getField(item, 'clientNumber') || '').toString().trim();
          const clientEmail = (getField(item, 'clientEmail') || '').toString().trim();
          const address = (getField(item, 'address') || '').toString().trim();
          const gender = (getField(item, 'gender') || '').toString().trim();
          const birthday = (getField(item, 'birthday') || '').toString().trim();
          const anniversary = (getField(item, 'anniversary') || '').toString().trim();
          const clientGstNumber = (getField(item, 'clientGstNumber') || '').toString().trim();
          const clientPos = (getField(item, 'clientPos') || '').toString().trim();

          const row = {
            rowIndex: idx + 2,
            clientName,
            clientNumber,
            clientEmail,
            address,
            gender,
            birthday,
            anniversary,
            clientGstNumber,
            clientPos,
          };

          const missingFields = [];
          if (!row.clientName) missingFields.push('clientName');
          if (!row.clientNumber || normNumber(row.clientNumber).length < 10) missingFields.push('clientNumber');

          row.invalid = missingFields.length > 0;
          row.invalidReason = missingFields.length ? `Missing/invalid: ${missingFields.join(', ')}` : '';
          return row;
        });

        mapped = buildFlags(mapped);
        setParsedRows(mapped);

        // by default select every valid row EXCEPT the ones that already exist,
        // so the user has to consciously opt-in to updating an existing client
        setSelectedRows(
          mapped.filter((r) => !r.invalid && !r.alreadyExists).map((r) => r.rowIndex)
        );
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .xls file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveRow = (rowIndex) => {
    setParsedRows((prev) => buildFlags(prev.filter((r) => r.rowIndex !== rowIndex)));
    setSelectedRows((prev) => prev.filter((i) => i !== rowIndex));
  };

  const handleRemoveAllFileDuplicates = () => {
    const seen = new Set();
    const deduped = [];
    parsedRows.forEach((r) => {
      if (r.invalid) {
        deduped.push(r);
        return;
      }
      const key = duplicateKey(r);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(r);
    });
    const removedCount = parsedRows.length - deduped.length;
    setParsedRows(buildFlags(deduped));
    setSelectedRows((prev) => prev.filter((i) => deduped.some((r) => r.rowIndex === i)));
    setShowMessage?.({
      message: removedCount > 0 ? `Removed ${removedCount} duplicate row(s)` : 'No duplicates found in file',
      messageType: removedCount > 0 ? 'success' : 'error',
    });
  };

  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : selectableRows.map((r) => r.rowIndex));
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const fileDuplicateCount = parsedRows.filter((r) => r.isDuplicateInFile).length;
  const alreadyExistsCount = parsedRows.filter((r) => r.alreadyExists).length;
  const readyCount = selectableRows.length;

  const handleSubmit = async () => {
    if (parsedRows.length === 0) {
      setShowMessage({ message: 'Upload a valid file', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({ message: 'Select at least one row to upload', messageType: 'error' });
      return;
    }

    const rowsToUpload = parsedRows
      .filter((r) => selectedRows.includes(r.rowIndex))
      .map((r) => {
        const payloadRow = {
          clientName: r.clientName,
          clientNumber: r.clientNumber,
          clientEmail: r.clientEmail,
          address: r.address,
          gender: r.gender,
          birthday: r.birthday,
          anniversary: r.anniversary,
        };
        if (branchData?.gstNumber) {
          payloadRow.clientGstNumber = r.clientGstNumber;
          payloadRow.clientPos = r.clientPos;
        }
        return payloadRow;
      });

    setSubmitting(true);
    try {
      const res = await createManyClient(activeBranchId, JSON.stringify({ bulkData: rowsToUpload }));
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Uploaded successfully', messageType: 'success' });
        handleClose();
        onUploaded?.();
      } else {
        setShowMessage({ message: res?.message || 'Upload failed', messageType: 'error' });
      }
    } catch (err) {
      setShowMessage({ message: err.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        target="_blank"
        startIcon={<MdCloudUpload />}
        sx={{ background: '#6174DD', height: '40px', minWidth: '160px' }}
        onClick={handleOpen}
      >
        Bulk Client Upload
      </Button>

      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        maxWidth={parsedRows.length > 0 ? 'lg' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          Bulk Upload Clients{' '}
          <Tooltip
            title="Upload an excel file with columns: clientName, clientNumber, clientEmail, address, gender, birthday, anniversary. Rows with a mobile number that already exists are flagged — uploading them will UPDATE that client's existing details. Uncheck any row you don't want touched."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={downloadExcelTemplate}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {alreadyExistsCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {alreadyExistsCount} client(s) in this file already exist (matched by mobile number). They are
              unchecked by default — if you leave them checked and upload, their existing details will be
              <strong> updated/overwritten</strong> with the values from this file. Uncheck any you don't want to touch.
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${readyCount} valid`} color="success" />
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" />}
                {fileDuplicateCount > 0 && <Chip label={`${fileDuplicateCount} duplicate rows in file`} color="warning" />}
                {alreadyExistsCount > 0 && (
                  <Chip label={`${alreadyExistsCount} already present`} color="info" />
                )}
                <Chip label={`${selectedRows.length} selected`} color="primary" variant="outlined" />
                {fileDuplicateCount > 0 && (
                  <Button size="small" color="warning" variant="outlined" onClick={handleRemoveAllFileDuplicates}>
                    Remove file duplicates (keep first)
                  </Button>
                )}
              </Box>

              <Box sx={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Birthday</TableCell>
                      <TableCell>Anniversary</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid
                            ? '#ffebee'
                            : r.alreadyExists
                            ? '#e3f2fd'
                            : r.isDuplicateInFile
                            ? '#fff3e0'
                            : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.clientName || '—'}</TableCell>
                        <TableCell>{r.clientNumber || '—'}</TableCell>
                        <TableCell>{r.clientEmail || '—'}</TableCell>
                        <TableCell>{r.gender || '—'}</TableCell>
                        <TableCell>{r.birthday || '—'}</TableCell>
                        <TableCell>{r.anniversary || '—'}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Tooltip title={r.invalidReason || 'Invalid row'}>
                              <Chip size="small" label="Invalid" color="error" />
                            </Tooltip>
                          ) : r.alreadyExists ? (
                            <Chip size="small" label="Already exists" color="info" />
                          ) : r.isDuplicateInFile ? (
                            <Chip size="small" label="Duplicate in file" color="warning" />
                          ) : (
                            <Chip size="small" label="New" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={r.invalid ? 'error' : 'text.secondary'}>
                            {r.invalid
                              ? r.invalidReason
                              : r.alreadyExists
                              ? 'Mobile number matches an existing client — will update if checked'
                              : r.isDuplicateInFile
                              ? 'Same number as another row in this file'
                              : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveRow(r.rowIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Selected Client(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadClientDialog;